import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  Box,
  MessageSquare,
  Package,
  Radar,
  Radio,
  Timer,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { AppIcon } from "@/components/shared/app-icon";
import { ReleaseEntityLink } from "@/components/shared/entity-link";
import { SourceAnomalyBadge } from "@/components/shared/security-signals";
import { StatusBadge } from "@/components/shared/status-badge";
import { TimeAgo } from "@/components/shared/time-ago";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  appsSearch,
  atRiskSourceSearch,
  discoveredSearch,
  failedEnrichmentSearch,
  feedbackSearch,
  pendingEnrichmentSearch,
  releasesSearch,
  reviewSearch,
} from "@/features/homepage/search-presets";
import { jobsFailuresSearch, jobsRunsSearch } from "@/features/jobs/search";
import { useHomepage } from "@/hooks/use-homepage";
import { formatDuration } from "@/lib/format-duration";
import { getJobFailureTypeLabel } from "@/lib/security-signals";
import type {
  AtRiskSourceItem,
  CatalogSuggestion,
  DashboardHomepageData,
  FeedbackListItem,
  HomepageDiscoveryItem,
  HomepageRunItem,
  JobFailureListItem,
  ReleaseListItem,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const EMPTY_HOMEPAGE_DATA: DashboardHomepageData = {
  overview: {
    needsAttention: {
      pendingCatalogSuggestions: 0,
      pendingDiscoveredApps: 0,
      pendingFeedback: 0,
      openFailures: 0,
    },
    sourceHealth: {
      activeSources: 0,
      errorSources: 0,
      staleSources: 0,
    },
    catalogContext: {
      publicApps: 0,
      totalApps: 0,
      recentReleases: 0,
    },
    enrichmentHealth: {
      pendingEnrichment: 0,
      enriched: 0,
      failed: 0,
      inProgress: 0,
    },
  },
  pendingSuggestions: [],
  pendingDiscoveries: [],
  newFeedback: [],
  openFailures: [],
  atRiskSources: [],
  recentRuns: [],
  recentReleases: [],
};

const jobTypeLabels: Record<HomepageRunItem["jobType"], string> = {
  poll_sources: "Poll Sources",
  cask_index_sync: "Cask Index Sync",
  enrich_discovered_apps: "Enrich Discoveries",
};

export const Route = createFileRoute("/")({
  component: DashboardPage,
});

function DashboardPage() {
  const { data, isLoading } = useHomepage();
  const homepage = data ?? EMPTY_HOMEPAGE_DATA;

  const attentionTotal =
    homepage.overview.needsAttention.pendingCatalogSuggestions +
    homepage.overview.needsAttention.pendingDiscoveredApps +
    homepage.overview.needsAttention.pendingFeedback +
    homepage.overview.needsAttention.openFailures;
  const sourceAttentionTotal =
    homepage.overview.sourceHealth.errorSources + homepage.overview.sourceHealth.staleSources;

  return (
    <div>
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Dashboard</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          <HeroPill label="Attention items" value={attentionTotal} tone="amber" />
          <HeroPill label="Source Attention" value={sourceAttentionTotal} tone="red" />
          <HeroPill
            label="Releases this week"
            value={homepage.overview.catalogContext.recentReleases}
            tone="blue"
          />
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <OverviewCard
          title="Needs Attention"
          description="Open queues across review, discovery, feedback, and failures."
          icon={Workflow}
          value={attentionTotal}
          valueLabel="items waiting"
          tone="amber"
          isLoading={isLoading}
        >
          <Link
            to="/review"
            search={reviewSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Catalog review" tone="amber" />
            <MetricValue value={homepage.overview.needsAttention.pendingCatalogSuggestions} />
          </Link>
          <Link
            to="/discovered-apps"
            search={discoveredSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Discoveries" tone="amber" />
            <MetricValue value={homepage.overview.needsAttention.pendingDiscoveredApps} />
          </Link>
          <Link
            to="/discovered-apps"
            search={pendingEnrichmentSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Pending enrichment" tone="slate" />
            <MetricValue value={homepage.overview.enrichmentHealth.pendingEnrichment} />
          </Link>
          <Link
            to="/discovered-apps"
            search={failedEnrichmentSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Failed enrichment" tone="red" />
            <MetricValue value={homepage.overview.enrichmentHealth.failed} />
          </Link>
          <Link
            to="/feedback"
            search={feedbackSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="New feedback" tone="blue" />
            <MetricValue value={homepage.overview.needsAttention.pendingFeedback} />
          </Link>
          <Link
            to="/jobs/failures"
            search={jobsFailuresSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Open failures" tone="red" />
            <MetricValue value={homepage.overview.needsAttention.openFailures} />
          </Link>
        </OverviewCard>

        <OverviewCard
          title="Source Health"
          description="Source coverage is only useful when feeds are current and error-free."
          icon={Radio}
          value={sourceAttentionTotal}
          valueLabel="sources need attention"
          tone={sourceAttentionTotal > 0 ? "red" : "emerald"}
          isLoading={isLoading}
        >
          <Link
            to="/sources"
            search={{ page: 1, pageSize: 25, status: "active", type: "all" }}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Active sources" tone="emerald" />
            <MetricValue value={homepage.overview.sourceHealth.activeSources} />
          </Link>
          <Link
            to="/sources"
            search={{ page: 1, pageSize: 25, status: "error", type: "all" }}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Error sources" tone="red" />
            <MetricValue value={homepage.overview.sourceHealth.errorSources} />
          </Link>
          <Link
            to="/sources"
            search={atRiskSourceSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Overdue sources" tone="amber" />
            <MetricValue value={homepage.overview.sourceHealth.staleSources} />
          </Link>
        </OverviewCard>

        <OverviewCard
          title="Catalog Context"
          description="Supporting context for what the operators are maintaining."
          icon={Box}
          value={homepage.overview.catalogContext.recentReleases}
          valueLabel="releases ingested in 7d"
          tone="blue"
          isLoading={isLoading}
        >
          <Link
            to="/apps"
            search={appsSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Public apps" tone="emerald" />
            <MetricValue value={homepage.overview.catalogContext.publicApps} />
          </Link>
          <Link
            to="/apps"
            search={appsSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Catalog apps" tone="slate" />
            <MetricValue value={homepage.overview.catalogContext.totalApps} />
          </Link>
          <Link
            to="/releases"
            search={releasesSearch}
            className="flex items-center justify-between rounded-lg border border-transparent px-3 py-2 transition-colors hover:border-border hover:bg-background/80"
          >
            <MetricLabel label="Recent releases" tone="blue" />
            <MetricValue value={homepage.overview.catalogContext.recentReleases} />
          </Link>
        </OverviewCard>
      </div>

      <div className="mt-7 grid gap-4 xl:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]">
        <div className="space-y-4">
          <SectionCard
            title="Catalog Review"
            description="Failed suggestions first, then the oldest pending review items."
            icon={Workflow}
            action={
              <Link to="/review" search={reviewSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={3} />
            ) : homepage.pendingSuggestions.length === 0 ? (
              <CompactEmptyState message="No review suggestions need attention." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.pendingSuggestions.map((item) => (
                  <CatalogSuggestionRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="High-Confidence Discoveries"
            description="Pending discoveries ranked by confidence and sighting volume."
            icon={Radar}
            action={
              <Link to="/discovered-apps" search={discoveredSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={3} />
            ) : homepage.pendingDiscoveries.length === 0 ? (
              <CompactEmptyState message="No pending discoveries." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.pendingDiscoveries.map((item) => (
                  <DiscoveryRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="New Feedback"
            description="Fresh client reports that have not been triaged yet."
            icon={MessageSquare}
            action={
              <Link to="/feedback" search={feedbackSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={3} />
            ) : homepage.newFeedback.length === 0 ? (
              <CompactEmptyState message="No new feedback." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.newFeedback.map((item) => (
                  <FeedbackRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard
            title="At-Risk Sources"
            description="Erroring sources and feeds that have missed their fetch interval."
            icon={AlertTriangle}
            action={
              <Link to="/sources" search={atRiskSourceSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={4} />
            ) : homepage.atRiskSources.length === 0 ? (
              <CompactEmptyState message="No at-risk sources." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.atRiskSources.map((item) => (
                  <AtRiskSourceRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Open Job Failures"
            description="Newest unresolved job failures with related entities."
            icon={Package}
            action={
              <Link to="/jobs/failures" search={jobsFailuresSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={3} />
            ) : homepage.openFailures.length === 0 ? (
              <CompactEmptyState message="No open job failures." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.openFailures.map((item) => (
                  <JobFailureRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Recent Job Runs"
            description="Latest poll and sync executions, including failures."
            icon={Timer}
            action={
              <Link to="/jobs/runs" search={jobsRunsSearch} className={viewAllClassName}>
                View all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            }
          >
            {isLoading ? (
              <ListSkeleton rows={3} />
            ) : homepage.recentRuns.length === 0 ? (
              <CompactEmptyState message="No recent runs." />
            ) : (
              <div className="space-y-2 p-2">
                {homepage.recentRuns.map((item) => (
                  <RunRow key={item.id} item={item} />
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      <div className="mt-4">
        <SectionCard
          title="Recent Releases"
          description="Most recently ingested active releases across the catalog."
          icon={Package}
          action={
            <Link to="/releases" search={releasesSearch} className={viewAllClassName}>
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        >
          {isLoading ? (
            <ListSkeleton rows={4} />
          ) : homepage.recentReleases.length === 0 ? (
            <CompactEmptyState message="No active releases yet." />
          ) : (
            <div className="space-y-2 p-2">
              {homepage.recentReleases.map((item) => (
                <ReleaseRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function OverviewCard({
  title,
  description,
  icon: Icon,
  value,
  valueLabel,
  tone,
  isLoading,
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  value: number;
  valueLabel: string;
  tone: "amber" | "blue" | "emerald" | "red";
  isLoading?: boolean;
  children: ReactNode;
}) {
  return (
    <Card
      className={cn(
        "gap-0 overflow-hidden rounded-2xl border shadow-[0_1px_0_rgba(255,255,255,0.03),0_18px_40px_-28px_rgba(0,0,0,0.7)] backdrop-blur-sm",
        tone === "amber" &&
          "border-amber-500/25 bg-linear-to-br from-amber-500/9 via-card to-card before:absolute",
        tone === "blue" && "border-sky-500/25 bg-linear-to-br from-sky-500/9 via-card to-card",
        tone === "emerald" &&
          "border-emerald-500/25 bg-linear-to-br from-emerald-500/9 via-card to-card",
        tone === "red" && "border-red-500/25 bg-linear-to-br from-red-500/9 via-card to-card",
      )}
    >
      <CardHeader className="border-b border-border/60 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {title}
            </p>
            <CardDescription className="mt-2 max-w-xs text-sm leading-6">
              {description}
            </CardDescription>
          </div>
          <div
            className={cn(
              "rounded-2xl border p-3 shadow-inner",
              tone === "amber" && "border-amber-500/25 bg-amber-500/8 text-amber-700",
              tone === "blue" && "border-sky-500/25 bg-sky-500/8 text-sky-700",
              tone === "emerald" && "border-emerald-500/25 bg-emerald-500/8 text-emerald-700",
              tone === "red" && "border-red-500/25 bg-red-500/8 text-red-700",
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">
        {isLoading ? (
          <>
            <Skeleton className="h-10 w-32" />
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </>
        ) : (
          <>
            <div className="rounded-2xl border border-border/60 bg-background/55 px-4 py-4 shadow-inner">
              <div className="flex items-baseline gap-3">
                <div className="text-3xl font-semibold tabular-nums">{value}</div>
                <div className="text-sm text-muted-foreground">{valueLabel}</div>
              </div>
            </div>
            <div className="space-y-1.5">{children}</div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SectionCard({
  title,
  description,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="gap-0 overflow-hidden rounded-2xl border-border/75 bg-linear-to-b from-muted/16 via-card to-card shadow-[0_1px_0_rgba(255,255,255,0.03),0_20px_38px_-30px_rgba(0,0,0,0.9)]">
      <CardHeader className="border-b border-border/60 pb-4">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl border border-border/70 bg-background/55 p-2.5 text-muted-foreground shadow-inner">
            <Icon className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold tracking-tight">{title}</CardTitle>
            <CardDescription className="max-w-xl leading-6">{description}</CardDescription>
          </div>
        </div>
        {action ? <CardAction>{action}</CardAction> : null}
      </CardHeader>
      <CardContent className="px-0 pb-1">{children}</CardContent>
    </Card>
  );
}

function CatalogSuggestionRow({ item }: { item: CatalogSuggestion }) {
  const app = item.app ?? item.source?.app;

  return (
    <Link
      to="/review"
      search={{ ...reviewSearch, status: item.status }}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={item.queueType} className="capitalize" />
          <StatusBadge status={item.status} />
          <Badge variant="outline">{item.evidenceCount} evidence</Badge>
        </div>
        <div className="mt-2 truncate font-medium">{item.title}</div>
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <AppIcon
            iconR2Key={app?.iconR2Key ?? null}
            appName={app?.canonicalName ?? item.title}
            size={24}
          />
          <span className="truncate">{app?.canonicalName ?? "Unlinked suggestion"}</span>
          {item.source ? (
            <span className="truncate text-xs">
              source: {item.source.label ?? item.source.sourceType}
            </span>
          ) : null}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        <div>
          first seen <TimeAgo date={item.firstSeenAt} />
        </div>
        <div className="mt-1">
          last seen <TimeAgo date={item.lastSeenAt} />
        </div>
      </div>
    </Link>
  );
}

function DiscoveryRow({ item }: { item: HomepageDiscoveryItem }) {
  const version = item.enrichedLatestVersion ?? item.homebrewCaskVersion;

  return (
    <Link
      to="/discovered-apps"
      search={discoveredSearch}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex items-start gap-3">
        <AppIcon iconR2Key={item.iconR2Key} appName={item.appName} size={28} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate font-medium">{item.appName}</div>
            <ConfidenceBadge score={item.confidenceScore} />
          </div>
          <div className="mt-1 truncate text-xs text-muted-foreground">
            {item.bundleId ?? "No bundle identifier"}{" "}
            {item.enrichedVendorName ? `· ${item.enrichedVendorName}` : ""}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{item.sightingCount} sightings</Badge>
            {item.sourceValidationStatus === "valid" ? (
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-700">
                feed ok
              </Badge>
            ) : null}
            {item.masAppId ? <Badge variant="outline">App Store ID: {item.masAppId}</Badge> : null}
            {item.sparkleFeedUrl ? <Badge variant="outline">Sparkle</Badge> : null}
            {item.electronUpdateUrl ? (
              <Badge variant="outline">
                {item.electronUpdateProvider
                  ? `Electron: ${item.electronUpdateProvider}`
                  : "Electron feed"}
              </Badge>
            ) : null}
            {item.homebrewCaskToken ? (
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-700">
                brew: {item.homebrewCaskToken}
              </Badge>
            ) : null}
            {item.minMacOSVersion ? (
              <Badge variant="outline">min macOS {item.minMacOSVersion}</Badge>
            ) : null}
            {version ? <Badge variant="outline">latest {version}</Badge> : null}
          </div>
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        <div>
          last seen <TimeAgo date={item.lastSeenAt} />
        </div>
      </div>
    </Link>
  );
}

function FeedbackRow({ item }: { item: FeedbackListItem }) {
  return (
    <Link
      to="/feedback"
      search={feedbackSearch}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="capitalize">
            {item.feedbackType.replaceAll("_", " ")}
          </Badge>
          <StatusBadge status={item.status} />
        </div>
        <div className="mt-2">
          {item.targetApp ? (
            <div className="flex items-start gap-3">
              <AppIcon
                iconR2Key={item.targetApp.iconR2Key}
                appName={item.targetApp.canonicalName}
                size={28}
              />
              <div className="min-w-0">
                <div className="truncate font-medium">{item.targetApp.canonicalName}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {item.targetApp.vendorName ?? item.targetApp.slug}
                </div>
              </div>
            </div>
          ) : (
            <div className="min-w-0">
              <div className="truncate font-medium">{item.appName ?? "Unknown app"}</div>
              <div className="truncate text-xs text-muted-foreground">
                {item.bundleId ?? "No bundle identifier"}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        <TimeAgo date={item.createdAt} />
      </div>
    </Link>
  );
}

function AtRiskSourceRow({ item }: { item: AtRiskSourceItem }) {
  return (
    <Link
      to="/sources/$sourceId"
      params={{ sourceId: item.id }}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {item.risk === "error" ? (
            <StatusBadge status="error" />
          ) : (
            <Badge variant="outline" className="border-amber-500/30 text-amber-700">
              overdue
            </Badge>
          )}
          <StatusBadge status={item.status} />
        </div>
        <div className="mt-2 min-w-0">
          <div className="truncate font-medium">{item.label ?? item.sourceType}</div>
          <div className="truncate text-xs text-muted-foreground">
            {item.app?.canonicalName ?? "Unlinked source"} · {item.parserKey} · {item.sourceType}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{item.pollIntervalMinutes}m interval</span>
          <span>·</span>
          <span>
            {item.lastFetchedAt ? (
              <>
                last fetched <TimeAgo date={item.lastFetchedAt} />
              </>
            ) : (
              "never fetched"
            )}
          </span>
          {item.lastSuccessAt ? (
            <>
              <span>·</span>
              <span>
                last success <TimeAgo date={item.lastSuccessAt} />
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        {item.risk === "error" ? (
          <div>
            failed {item.lastFailureAt ? <TimeAgo date={item.lastFailureAt} /> : "recently"}
          </div>
        ) : (
          <div>{formatOverdue(item.overdueMinutes)} overdue</div>
        )}
      </div>
    </Link>
  );
}

function JobFailureRow({ item }: { item: JobFailureListItem }) {
  const search =
    item.jobType === "source-anomaly"
      ? {
          ...jobsFailuresSearch,
          status: item.status,
          jobType: "source-anomaly" as const,
          failureId: item.id,
        }
      : {
          ...jobsFailuresSearch,
          status: item.status,
          failureId: item.id,
        };

  return (
    <Link
      to="/jobs/failures"
      search={search}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {item.jobType === "source-anomaly" ? (
            <SourceAnomalyBadge jobKey={item.jobKey} />
          ) : (
            <Badge variant="outline">{getJobFailureTypeLabel(item.jobType, item.jobKey)}</Badge>
          )}
          <StatusBadge status={item.status} />
        </div>
        {item.relatedRef ? (
          <div className="mt-2 min-w-0">
            <div className="truncate text-sm font-medium">{item.relatedRef.label}</div>
            {item.relatedRef.description ? (
              <div className="truncate text-xs text-muted-foreground">
                {item.relatedRef.description}
              </div>
            ) : null}
          </div>
        ) : null}
        {item.errorMessage ? (
          <div className="mt-2 line-clamp-2 text-xs text-red-600 dark:text-red-400">
            {item.errorMessage}
          </div>
        ) : null}
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        <TimeAgo date={item.createdAt} />
      </div>
    </Link>
  );
}

function RunRow({ item }: { item: HomepageRunItem }) {
  return (
    <Link
      to="/jobs/runs"
      search={jobsRunsSearch}
      className="group flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={item.status} />
          <Badge variant="outline">{jobTypeLabels[item.jobType]}</Badge>
          <Badge variant="outline" className="capitalize">
            {item.trigger}
          </Badge>
        </div>
        <div className="mt-2 text-sm">
          {item.itemsQueued == null ? (
            <span className="text-muted-foreground">No item count recorded.</span>
          ) : item.itemsTotal != null ? (
            <span>
              Queued <span className="font-medium">{item.itemsQueued}</span> of{" "}
              <span className="font-medium">{item.itemsTotal}</span>
            </span>
          ) : (
            <span>
              Queued <span className="font-medium">{item.itemsQueued}</span>
            </span>
          )}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          duration {formatDuration(item.startedAt, item.completedAt)}
          {item.actorId ? ` · ${item.actorId}` : " · system"}
        </div>
        {item.errorMessage ? (
          <div className="mt-2 line-clamp-2 text-xs text-red-600 dark:text-red-400">
            {item.errorMessage}
          </div>
        ) : null}
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground transition-transform group-hover:-translate-y-0.5">
        <TimeAgo date={item.startedAt} />
      </div>
    </Link>
  );
}

function ReleaseRow({ item }: { item: ReleaseListItem }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-transparent bg-background/20 px-4 py-4 transition-all hover:border-border/70 hover:bg-background/60">
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <AppIcon
            iconR2Key={item.app?.iconR2Key ?? null}
            appName={item.app?.canonicalName ?? item.versionRaw}
            size={28}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <ReleaseEntityLink
                className="min-w-0"
                release={{
                  id: item.id,
                  versionRaw: item.versionRaw,
                  channel: item.channel,
                  status: item.status,
                  isPrerelease: item.isPrerelease,
                  releasedAt: item.releasedAt,
                  app: item.app,
                }}
              />
              <StatusBadge status={item.channel} />
              {item.isLatestForChannel ? <Badge variant="outline">latest</Badge> : null}
              {item.isPinnedLatest ? <Badge variant="outline">pinned</Badge> : null}
            </div>
            <div className="mt-2 truncate text-sm text-muted-foreground">
              {item.app?.canonicalName ?? "Unlinked release"}
            </div>
          </div>
        </div>
      </div>
      <div className="shrink-0 text-right text-xs text-muted-foreground">
        <div>
          ingested <TimeAgo date={item.createdAt} />
        </div>
        {item.releasedAt ? (
          <div className="mt-1">
            released <TimeAgo date={item.releasedAt} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MetricLabel({
  label,
  tone,
}: {
  label: string;
  tone: "amber" | "blue" | "emerald" | "red" | "slate";
}) {
  return (
    <div className="flex items-center gap-2 text-sm text-foreground/95">
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          tone === "amber" && "bg-amber-500",
          tone === "blue" && "bg-sky-500",
          tone === "emerald" && "bg-emerald-500",
          tone === "red" && "bg-red-500",
          tone === "slate" && "bg-slate-500",
        )}
      />
      <span>{label}</span>
    </div>
  );
}

function MetricValue({ value }: { value: number }) {
  return <span className="text-sm font-semibold tabular-nums">{value}</span>;
}

function ConfidenceBadge({ score }: { score: number | null }) {
  if (score === null) {
    return <Badge variant="outline">score --</Badge>;
  }

  return (
    <Badge
      variant="outline"
      className={cn(
        score >= 70 && "border-emerald-500/30 text-emerald-700",
        score >= 40 && score < 70 && "border-amber-500/30 text-amber-700",
        score < 40 && "border-red-500/30 text-red-700",
      )}
    >
      score {score}
    </Badge>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 px-6 py-4">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-[4.5rem] w-full rounded-xl" />
      ))}
    </div>
  );
}

function CompactEmptyState({ message }: { message: string }) {
  return (
    <div className="px-4 py-10">
      <div className="rounded-2xl border border-dashed border-border/70 bg-background/25 px-4 py-8 text-center">
        <div className="text-sm font-medium text-foreground/90">{message}</div>
        <div className="mt-1 text-xs text-muted-foreground">
          This panel will repopulate automatically as new activity arrives.
        </div>
      </div>
    </div>
  );
}

function HeroPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "amber" | "blue" | "red";
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-full border border-border/70 bg-card/70 px-3.5 py-2 text-sm shadow-sm backdrop-blur-sm">
      <span
        className={cn(
          "h-2.5 w-2.5 rounded-full",
          tone === "amber" && "bg-amber-500",
          tone === "blue" && "bg-sky-500",
          tone === "red" && "bg-red-500",
        )}
      />
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function formatOverdue(overdueMinutes: number | null) {
  if (overdueMinutes == null) {
    return "unknown";
  }

  if (overdueMinutes < 60) {
    return `${overdueMinutes}m`;
  }

  const hours = overdueMinutes / 60;
  if (hours < 24) {
    return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
  }

  return `${(hours / 24).toFixed(1)}d`;
}

const viewAllClassName =
  "inline-flex items-center gap-1 rounded-full border border-border/70 bg-background/45 px-3 py-1.5 text-sm text-muted-foreground transition-all hover:border-border hover:bg-background/75 hover:text-foreground";
